import React, { useEffect, useState } from "react";
import {
    Col, Container, Row, Card, CardBody,
    Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Badge
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Loader from "../../components/Common/Loader";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

export default function SubUserManagement() {
    const { companyId } = useParams();
    const navigate = useNavigate();

    let baseUrl = process.env.REACT_APP_STATUS === "PROD"
        ? process.env.REACT_APP_PROD_URL
        : (process.env.REACT_APP_STATUS === "STAGE"
            ? process.env.REACT_APP_STAGE_URL
            : process.env.REACT_APP_DEV_URL);

    const [subUsers, setSubUsers] = useState([]);
    const [companyDevices, setCompanyDevices] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedDevices, setSelectedDevices] = useState([]);

    useEffect(() => {
        fetchSubUsers();
        fetchCompanyDevices();

        const checkRoleAndRedirect = async () => {
            try {
                const token = { token: localStorage.getItem("token") };
                const response = await axios.post(`${baseUrl}/api/v1/dashboard/getTokenDetails`, token);
                if (response.data.role === 'SUB_USER' || response.data.role === 'company-subuser') {
                    navigate('/dashboard');
                }
            } catch (err) {
                console.error("Failed to check role", err);
            }
        };
        checkRoleAndRedirect();
    }, [companyId]);

    const fetchSubUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(
                `${baseUrl}/api/v1/companyAuth/subusers/${companyId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSubUsers(res.data.subUsers || []);
        } catch (err) {
            console.error("Failed to fetch sub-users", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyDevices = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(
                `${baseUrl}/api/v1/devices/devices-list/${companyId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCompanyDevices(res.data.companydevices || []);
        } catch (err) {
            console.error("Failed to fetch company devices", err);
        }
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        if (user.assigned_devices && user.assigned_devices.length > 0) {
            // Map assigned devices to array of IDs if they are hydrated objects
            const ids = user.assigned_devices.map(d => typeof d === 'object' ? d._id : d);
            setSelectedDevices(ids);
        } else {
            setSelectedDevices([]);
        }
        setEditModalOpen(true);
    };

    const toggleDeviceSelection = (deviceId) => {
        setSelectedDevices(prev =>
            prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
        );
    };

    const handleUpdateDevices = async () => {
        try {
            const token = localStorage.getItem("token");
            await axios.patch(
                `${baseUrl}/api/v1/companyAuth/subuser-devices/${editingUser._id}`,
                { assigned_devices: selectedDevices },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setEditModalOpen(false);
            fetchSubUsers(); // refresh data
        } catch (err) {
            console.error("Failed to update devices for sub-user", err);
        }
    };

    const handleRemoveUser = async (userId) => {
        if (!window.confirm("Are you sure you want to remove this user from the company?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(
                `${baseUrl}/api/v1/companyAuth/subuser/${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchSubUsers();
        } catch (err) {
            console.error("Failed to remove sub-user", err);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid={true}>
                    <Breadcrumbs title="Companies" breadcrumbItem="Sub-User Management" />

                    <Row className="mb-3">
                        <Col sm={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5>Sub-Users & Assignees</h5>
                                <Button color="primary" onClick={() => navigate(`/companydevices/${companyId}`)}>Back to Devices</Button>
                            </div>
                        </Col>
                    </Row>

                    {loading ? (
                        <Loader message="Loading sub-users..." />
                    ) : (
                        <Row>
                            <Col xl={12}>
                                <Card>
                                    <CardBody>
                                        <div className="table-responsive">
                                            <Table className="table-hover table-centered table-nowrap mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>User Name</th>
                                                        <th>Email</th>
                                                        <th>Role</th>
                                                        <th>Assigned Devices</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {subUsers.length > 0 ? (
                                                        subUsers.map((user) => (
                                                            <tr key={user._id}>
                                                                <td>{user.name}</td>
                                                                <td>{user.email}</td>
                                                                <td><Badge color="info">{user.role}</Badge></td>
                                                                <td>
                                                                    {user.assigned_devices?.length || 0} Device(s)
                                                                </td>
                                                                <td>
                                                                    <Button color="secondary" size="sm" className="me-2" onClick={() => openEditModal(user)}>
                                                                        Edit Devices
                                                                    </Button>
                                                                    <Button color="danger" size="sm" onClick={() => handleRemoveUser(user._id)}>
                                                                        Remove Access
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center">No assigned users found.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Edit Devices Modal */}
                    <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(!editModalOpen)}>
                        <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>
                            Edit Device Access for {editingUser?.name}
                        </ModalHeader>
                        <ModalBody>
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {companyDevices.length > 0 ? (
                                    <Table size="sm" className="mb-0">
                                        <tbody>
                                            {companyDevices.map(device => (
                                                <tr
                                                    key={device._id}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => toggleDeviceSelection(device._id)}
                                                >
                                                    <td style={{ width: "30px" }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDevices.includes(device._id)}
                                                            onChange={() => toggleDeviceSelection(device._id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td>{device.device_name}</td>
                                                    <td><small className="text-muted">{device.device_serialId}</small></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <p className="text-center text-muted mb-0">No devices found for this company.</p>
                                )}
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => setEditModalOpen(false)}>Cancel</Button>
                            <Button color="primary" onClick={handleUpdateDevices}>Save Changes</Button>
                        </ModalFooter>
                    </Modal>

                </Container>
            </div>
        </React.Fragment>
    );
}
